import { keyBetween, PositionCollisionError } from '@werft/domain';
import type { PageCommentDto, PageDto, PageVersionDto, TaskDto } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { ApiError, newOpId, newUuid } from '../../platform/api.js';
import { keys, queryClient } from '../../platform/queries.js';
import { upsertPage, upsertPageComment, upsertTask } from '../../platform/realtime.js';
import { resendWithSafeList, safeListHandled } from '../../platform/derive.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';
import { savedNote } from '../../platform/undo.js';

/**
 * Schreibwege der Notizen (Paket P12). Seiten (anlegen, umbenennen, verschieben, löschen), Kommentare und Versionen
 * laufen über den Ausgangskorb (offline, Overlay, Wiederholung mit derselben opId). Der Inhalt selbst läuft über
 * den Live-Raum (`collab.ts`). Seiten und Kommentare bekommen ihre ID im Browser – so lassen sie sich sofort öffnen
 * bzw. im Text markieren.
 */

const failed = (err: unknown) => {
  if (err instanceof ApiError && err.status === 404) return;
  toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });
};
const reloadPages = (pid: string) => {
  void queryClient.invalidateQueries({ queryKey: keys.pages(pid) });
  void queryClient.invalidateQueries({ queryKey: keys.pageTrash(pid) });
};

/** Absicht bei Versionskonflikt auf den neuen Stand setzen und erneut senden (höchstens dreimal) */
const rebase = {
  done: (e: { projectId: string }, r: unknown) => upsertPage(e.projectId, r as PageDto),
  fail: (e: { projectId: string; body: unknown; meta?: Record<string, unknown> }, err: unknown) => {
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as PageDto) : null;
    const n = ((e.meta ?? {}) as { rebases?: number }).rebases ?? 0;
    if (current && n < 3) return { opId: newOpId(), body: { ...(e.body as object), expectedVersion: current.version }, meta: { rebases: n + 1 } };
    reloadPages(e.projectId);
    failed(err);
  },
};
const plain = {
  done: (e: { projectId: string }, r: unknown) => upsertPage(e.projectId, r as PageDto),
  fail: (e: { projectId: string }, err: unknown) => (reloadPages(e.projectId), failed(err)),
};

outbox.register('page.create', plain);
outbox.register('page.update', rebase);
outbox.register('page.metadata', {
  done: plain.done,
  fail: (e, err) => {
    if (err instanceof ApiError && err.code === 'version_conflict' && err.body.current) upsertPage(e.projectId, err.body.current as PageDto);
    else reloadPages(e.projectId);
    failed(err);
  },
});
outbox.register('page.move', rebase);
outbox.register('page.archive', {
  done: (e, r) => (upsertPage(e.projectId, r as PageDto), reloadPages(e.projectId)),
  fail: (e, err) => (reloadPages(e.projectId), failed(err)),
});
outbox.register('page.comment', {
  done: (e, r) => upsertPageComment(e.projectId, r as PageCommentDto),
  fail: (e, err) => {
    void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'pagecomments'] });
    failed(err);
  },
});
outbox.register('page.comment.remove', {
  done: (e) => void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'pagecomments'] }),
  fail: (e, err) => (void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'pagecomments'] }), failed(err)),
});
// Projektinsel W11: Aufgabe aus markiertem Text (eine Operation mit Verknüpfung „Entstanden aus“)
outbox.register('page.task', {
  done: (e, r) => {
    upsertTask(e.projectId, (r as { task: TaskDto }).task);
    void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'links'] });
  },
  fail: (e, err) => void (safeListHandled(err, () => resendWithSafeList(e)) || failed(err)),
});

/** Dieselbe opId je Dialog: Doppelklick, Wiederholung nach Abbruch oder erneutes Senden legen keine zweite Aufgabe an */
export function taskFromText(projectId: string, pageId: string, opId: string, input: { title: string; listId?: string; quote: string }) {
  return outbox.enqueue<{ task: TaskDto }>({
    opId,
    kind: 'page.task',
    projectId,
    method: 'POST',
    url: `/api/pages/${pageId}/task`,
    body: { opId, title: input.title, ...(input.listId ? { listId: input.listId } : {}), quote: input.quote.slice(0, 5000) },
  });
}

outbox.register('page.version', {
  done: (e) => void queryClient.invalidateQueries({ queryKey: ['p', e.projectId, 'pageversions'] }),
  fail: (_e, err) => failed(err),
});

/* ---------- Seiten ---------- */

const byPos = (a: PageDto, b: PageDto) => (a.position < b.position ? -1 : a.position > b.position ? 1 : a.id < b.id ? -1 : 1);
export const childrenOf = (pages: PageDto[], parentId: string | null) => pages.filter((p) => p.parentId === parentId).sort(byPos);

function between(a: string | null, b: string | null): string {
  try {
    return keyBetween(a, b);
  } catch (err) {
    if (err instanceof PositionCollisionError) return keyBetween(a, null);
    throw err;
  }
}

/** Neue Seite (oberste Ebene oder Unterseite) ans Ende; liefert die ID zum sofortigen Öffnen */
export function createPage(projectId: string, pages: PageDto[], parentId: string | null, meId: string, title?: string, areaId: string | null = null, options: { template?: 'meeting' | 'decision'; noteDate?: string | null; tags?: string[] } = {}): string {
  const opId = newOpId();
  const id = newUuid();
  const siblings = childrenOf(pages, parentId);
  const now = new Date().toISOString();
  const page: PageDto = {
    id,
    projectId,
    parentId,
    title: title ?? t('docs.untitled'),
    noteDate: options.noteDate ?? null,
    tags: options.tags ?? [],
    position: between(siblings.at(-1)?.position ?? null, null),
    createdBy: meId,
    createdAt: now,
    updatedBy: meId,
    updatedByLink: null,
    updatedAt: now,
    archivedAt: null,
    areaId: parentId ? null : areaId,
    version: 0,
    reviewOwnerId: null,
    reviewedOn: null,
    reviewDue: null,
  };
  void outbox
    .enqueue({ opId, kind: 'page.create', projectId, method: 'POST', url: `/api/projects/${projectId}/pages`, body: { opId, id, parentId, ...options, ...(title ? { title } : {}), ...(!parentId && areaId ? { areaId } : {}) }, overlay: { type: 'pageCreate', page } })
    .catch(() => {});
  return id;
}

export function renamePage(p: PageDto, title: string) {
  if (title === p.title) return;
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'page.update', projectId: p.projectId, method: 'PATCH', url: `/api/pages/${p.id}`, body: { opId, expectedVersion: p.version, patch: { title } }, overlay: { type: 'pagePatch', pageId: p.id, fields: { title } } })
    .catch(() => {});
}

/** Metadatenkonflikte bleiben im Dialog sichtbar; fremde Änderungen nicht automatisch überschreiben. */
export function updateNoteMetadata(p: PageDto, patch: { noteDate?: string | null; tags?: string[] }) {
  const opId = newOpId();
  return outbox.enqueue({ opId, kind: 'page.metadata', projectId: p.projectId, method: 'PATCH', url: `/api/pages/${p.id}`, body: { opId, expectedVersion: p.version, patch }, overlay: { type: 'pagePatch', pageId: p.id, fields: patch } });
}

/** Verschieben unter `parentId`, vor `beforeId` (sonst ans Ende) */
export function movePage(p: PageDto, pages: PageDto[], parentId: string | null, beforeId: string | null) {
  const siblings = childrenOf(pages, parentId).filter((x) => x.id !== p.id);
  const i = beforeId ? siblings.findIndex((x) => x.id === beforeId) : -1;
  const position = i >= 0 ? between(siblings[i - 1]?.position ?? null, siblings[i]!.position) : between(siblings.at(-1)?.position ?? null, null);
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'page.move',
      projectId: p.projectId,
      method: 'POST',
      url: `/api/pages/${p.id}/move`,
      body: { opId, expectedVersion: p.version, parentId, ...(beforeId ? { beforeId } : {}) },
      overlay: { type: 'pagePatch', pageId: p.id, fields: { parentId, position } },
    })
    .catch(() => {});
}

/** Alle Nachfahren (für Löschen mit Unterseiten und „nicht unter sich selbst verschieben“) */
export function descendants(pages: PageDto[], id: string): string[] {
  const out: string[] = [];
  const walk = (pid: string) => {
    for (const c of pages) if (c.parentId === pid) (out.push(c.id), walk(c.id));
  };
  walk(id);
  return out;
}

/** In den Papierkorb (mit Unterseiten) – mit „Rückgängig“ */
export function archivePage(p: PageDto, pages: PageDto[]) {
  const opId = newOpId();
  const ids = [p.id, ...descendants(pages, p.id)];
  void outbox
    .enqueue({ opId, kind: 'page.archive', projectId: p.projectId, method: 'DELETE', url: `/api/pages/${p.id}`, body: { opId }, overlay: { type: 'pageRemove', pageIds: ids } })
    .catch(() => {});
  toast(ids.length > 1 ? t('docs.trashedWithSub', { title: p.title, n: ids.length - 1 }) : t('docs.trashed', { title: p.title }), {
    action: {
      label: t('common.undo'),
      run: () => {
        if (!outbox.cancel(opId)) restorePage(p);
      },
    },
  });
}

export function restorePage(p: PageDto) {
  const opId = newOpId();
  void outbox.enqueue({ opId, kind: 'page.archive', projectId: p.projectId, method: 'POST', url: `/api/pages/${p.id}/restore`, body: { opId } }).catch(() => {});
}

/* ---------- Versionen ---------- */

export function saveVersion(projectId: string, pageId: string, label?: string) {
  const opId = newOpId();
  const done = outbox.enqueue({ opId, kind: 'page.version', projectId, method: 'POST', url: `/api/pages/${pageId}/versions`, body: { opId, ...(label ? { label } : {}) } });
  // AF11: „gesichert“ erst nach Bestätigung; offline sagt die Rückmeldung, dass noch gewartet wird
  if (outbox.getStatus().waiting) toast(savedNote(t('docs.versionQueued')));
  done.then(
    (r) => {
      if (!r) return; // zurückgenommen
      toast(t('docs.versionSaved'));
      const title = queryClient.getQueryData<{ items: PageDto[] }>(keys.pages(projectId))?.items.find((x) => x.id === pageId)?.title ?? '';
      // AF09: auf Wunsch auf die Übersicht – der bewusste Abschluss ist der passende Moment
      void import('../hints/triggers.js').then((m) => m.afterSaveVersion(projectId, pageId, title || t('docs.untitled')));
    },
    () => {},
  );
}

export function restorePageVersion(projectId: string, pageId: string, v: PageVersionDto) {
  const opId = newOpId();
  void outbox.enqueue({ opId, kind: 'page.version', projectId, method: 'POST', url: `/api/pages/${pageId}/versions/${v.id}/restore`, body: { opId } }).catch(() => {});
  toast(t('docs.versionRestored', { n: v.number }));
}

/* ---------- Kommentare ---------- */

/** Neuer Thread (mit `quote`) oder Antwort (mit `threadId`); liefert die ID für die Markierung im Text */
export function addPageComment(projectId: string, pageId: string, meId: string, body: string, opts: { threadId?: string; quote?: string; id?: string }): string {
  const opId = newOpId();
  const id = opts.id ?? newUuid();
  const comment: PageCommentDto = {
    id,
    pageId,
    threadId: opts.threadId ?? id,
    authorId: meId,
    body,
    quote: opts.threadId ? null : (opts.quote ?? null),
    resolvedAt: null,
    resolvedBy: null,
    createdAt: new Date().toISOString(),
    editedAt: null,
    version: 0,
  };
  void outbox
    .enqueue({
      opId,
      kind: 'page.comment',
      projectId,
      method: 'POST',
      url: `/api/pages/${pageId}/comments`,
      body: { opId, id, body, ...(opts.threadId ? { threadId: opts.threadId } : {}), ...(opts.quote && !opts.threadId ? { quote: opts.quote.slice(0, 500) } : {}) },
      overlay: { type: 'pageComment', comment },
    })
    .catch(() => {});
  return id;
}

export function editPageComment(projectId: string, c: PageCommentDto, body: string) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'page.comment',
      projectId,
      method: 'PATCH',
      url: `/api/page-comments/${c.id}`,
      body: { opId, expectedVersion: c.version, patch: { body } },
      overlay: { type: 'pageCommentPatch', pageId: c.pageId, commentId: c.id, fields: { body, editedAt: new Date().toISOString() } },
    })
    .catch(() => {});
}

export function resolveThread(projectId: string, c: PageCommentDto, meId: string, resolved: boolean) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'page.comment',
      projectId,
      method: 'PATCH',
      url: `/api/page-comments/${c.id}`,
      body: { opId, expectedVersion: c.version, patch: { resolved } },
      overlay: { type: 'pageCommentPatch', pageId: c.pageId, commentId: c.id, fields: resolved ? { resolvedAt: new Date().toISOString(), resolvedBy: meId } : { resolvedAt: null, resolvedBy: null } },
    })
    .catch(() => {});
}

/** Löschen; der erste Kommentar nimmt den ganzen Thread mit */
export function removePageComment(projectId: string, c: PageCommentDto, all: PageCommentDto[]) {
  const opId = newOpId();
  const ids = c.id === c.threadId ? all.filter((x) => x.threadId === c.id).map((x) => x.id) : [c.id];
  void outbox
    .enqueue({ opId, kind: 'page.comment.remove', projectId, method: 'DELETE', url: `/api/page-comments/${c.id}`, body: { opId }, overlay: { type: 'pageCommentRemove', pageId: c.pageId, commentIds: ids } })
    .catch(() => {});
}

/* ---------- Bilder ---------- */

/** Bild hochladen (direkt, mit opId gegen Doppel bei Wiederholung); liefert die Adresse fürs Dokument */
export async function uploadPageImage(pageId: string, file: File): Promise<string> {
  const opId = newOpId();
  const types: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', mp4: 'video/mp4', webm: 'video/webm' };
  const res = await fetch(`/api/pages/${pageId}/assets?opId=${opId}`, {
    method: 'POST',
    headers: { 'x-werft-upload': '1', 'content-type': file.type || types[file.name.split('.').pop()!.toLowerCase()] || 'application/octet-stream' },
    body: file,
    credentials: 'same-origin',
  });
  const body = (await res.json().catch(() => null)) as { src?: string; message?: string } | null;
  if (!res.ok || !body?.src) throw new Error(body?.message ?? t('docs.mediaFailed'));
  return body.src;
}
